/**
 * speech-reference.js — «как у эталона» для начитки (window.SpeechReference)
 *
 * Просьба #6971 (16.10.2026, /acx-mastering): «прикрепить пример звучания
 * другой аудиокниги, на которую ровняться, как в музыкальном мастеринге».
 *
 * Чем это НЕ является. Музыкальный матчер (MasteringReference) копирует
 * тональную форму эталона целиком. На речи это копирует ГОЛОС: замер
 * 2026-10-22 на 13 чтецах LibriVox — чужой чтец как эталон просит 3.85 дБ RMS
 * правки и упирается в потолок 8 дБ в 52 парах из 90, а низ 80–400 Гц у пар
 * разного пола расходится на 3.8 дБ против 1.7 у двух мужчин (это высота и
 * грудной регистр, а не микрофон). Тот же голос в другой сессии матчер
 * подгоняет до пола метода: известная разница 2.5 дБ → остаток 1.12 дБ, когда
 * две половины одной записи без всякой разницы уже «просят» 1.10.
 *
 * Отсюда два режима:
 *   'self'  — эталон — своя же принятая глава: тон подгоняется полностью;
 *   'other' — другой чтец: тон только выше 400 Гц и на треть, низ < 70 Гц
 *             только срезается (гул — это запись, не голос).
 * Громкость, разброс громкости, шум в паузах и тишина по краям — обработка,
 * а не голос: они подгоняются в обоих режимах, и нормы ACX всегда сильнее
 * эталона. Темп и паузы — только сведения: менять чужую манеру чтения
 * автоматически нельзя.
 *
 * API (mono — Float32Array, sr — частота):
 *   measure(chunks, sr, opts) → profile      chunks — [Float32Array] куски записи
 *       opts.edges: {head, tail} | null      тишина по краям, если известна
 *       opts.label                           имя для показа
 *   gate(mono, sr) → {thr, noiseDb, speechDb90}
 *   edgeSilence(mono, sr, thr) → {head, tail}  секунды тишины в начале и конце
 *   compare(ours, ref, mode) → [{key, ours, ref, delta, act}]
 *   plan(ours, ref, mode, ctx) → {rmsTarget, nr, eq, lev, edges, voice, notes}
 *       ctx.targetBuffer — буфер-выдержка нашей записи (для подгонки тона)
 *       ctx.levelExcerpt — Float32Array выдержки (для калибровки компрессии)
 *   level(mono, sr, {thrDb, ratio}) → Float32Array   мягкая компрессия речи
 *   setEdges(mono, sr, thr, head, tail, tone) → Float32Array   тишина ровно head/tail
 *   excerpt(mono, sr, maxSec, spans) → [Float32Array]   куски для анализа
 *   toJSON(profile) / fromJSON(obj)          профиль ↔ localStorage
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.SpeechReference = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function (root) {
    'use strict';

    var VERSION = 1;
    var ACX = { rmsMin: -23, rmsMax: -18, noiseMax: -60, edgeMin: 1, edgeMax: 5 };
    // Подгонка ставит громкость не на саму границу окна ACX, а на 0,5 дБ внутрь:
    // RMS после кодирования в MP3 гуляет на сотые доли, и −23,0 ровно — это файл,
    // который проверка ACX может не пропустить (замер стенда 22.10: −23,0 → −23,0).
    var RMS_MARGIN = 0.5;

    function MR() {
        if (root && root.MasteringReference) return root.MasteringReference;
        if (typeof globalThis !== 'undefined' && globalThis.MasteringReference) return globalThis.MasteringReference;
        return null;
    }

    function db(v) { return 20 * Math.log10(v + 1e-10); }
    function pdb(p) { return 10 * Math.log10(p + 1e-20); }
    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function pct(sorted, q) {
        if (!sorted.length) return NaN;
        var i = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * q)));
        return sorted[i];
    }
    function sortNum(a) { return Array.prototype.slice.call(a).sort(function (x, y) { return x - y; }); }

    // RMS окнами (dB). win/hop в секундах.
    function frames(x, sr, win, hop) {
        var W = Math.max(1, Math.round(win * sr)), H = Math.max(1, Math.round(hop * sr));
        var n = x.length >= W ? Math.floor((x.length - W) / H) + 1 : 0;
        var out = new Float32Array(n);
        // скользящая сумма квадратов — O(n), а не O(n·W)
        var s = 0, i;
        for (i = 0; i < Math.min(W, x.length); i++) s += x[i] * x[i];
        for (var f = 0; f < n; f++) {
            if (f > 0) {
                var a = (f - 1) * H, b = a + W;
                for (i = a; i < a + H; i++) s -= x[i] * x[i];
                for (i = b; i < b + H && i < x.length; i++) s += x[i] * x[i];
            }
            out[f] = db(Math.sqrt(Math.max(0, s) / W));
        }
        return out;
    }

    /* Порог «речь / не речь». Речь — 90-й перцентиль окон 50 мс, шум — 5-й.
       Порог на 30 дБ ниже речи, но не ниже шума + 6: на чистой записи ловит
       тихие окончания слов, на шумной не принимает шум за речь. */
    function gate(mono, sr) {
        var w = sortNum(frames(mono, sr, 0.05, 0.05));
        var noise = pct(w, 0.05), speech90 = pct(w, 0.9);
        if (!isFinite(noise)) noise = -100;
        if (!isFinite(speech90)) speech90 = -100;
        return { thr: Math.max(speech90 - 30, noise + 6), noiseDb: noise, speechDb90: speech90 };
    }

    /* Шумовой пол — тем же способом, что и у самого тула и у ACX-проверки:
       мощностное среднее самых тихих 10 % окон по 50 мс. */
    function noiseFloor(mono, sr) {
        var W = Math.max(1, Math.floor(sr * 0.05)), arr = [];
        for (var i = 0; i + W <= mono.length; i += W) {
            var s = 0;
            for (var j = i; j < i + W; j++) s += mono[j] * mono[j];
            arr.push(s / W);
        }
        if (!arr.length) return -100;
        arr.sort(function (a, b) { return a - b; });
        var k = Math.max(1, Math.floor(arr.length * 0.1)), p = 0;
        for (var q = 0; q < k; q++) p += arr[q];
        return pdb(p / k);
    }

    // Простой RBJ-фильтр верхних частот второго порядка (для огибающей слогов).
    function highpass(x, sr, f0) {
        var w0 = 2 * Math.PI * f0 / sr, cw = Math.cos(w0), al = Math.sin(w0) / (2 * 0.7071);
        var b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2, a0 = 1 + al, a1 = -2 * cw, a2 = 1 - al;
        b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
        var y = new Float32Array(x.length), x1 = 0, x2 = 0, y1 = 0, y2 = 0;
        for (var i = 0; i < x.length; i++) {
            var v = b0 * x[i] + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
            x2 = x1; x1 = x[i]; y2 = y1; y1 = v; y[i] = v;
        }
        return y;
    }

    /* Основной тон: автокорреляция на громких кадрах 40 мс, 70–350 Гц, не
       больше `maxFrames` кадров на кусок (иначе 3 мин речи — секунды счёта). */
    function f0Of(x, sr, loudDb, out, maxFrames) {
        var W = Math.round(0.04 * sr), lo = Math.floor(sr / 350), hi = Math.ceil(sr / 70);
        var cand = [];
        for (var s = 0; s + W + hi < x.length; s += W) {
            var e = 0;
            for (var i = 0; i < W; i++) e += x[s + i] * x[s + i];
            if (db(Math.sqrt(e / W)) >= loudDb) cand.push(s);
        }
        var step = Math.max(1, Math.ceil(cand.length / (maxFrames || 120)));
        for (var c = 0; c < cand.length; c += step) {
            var st = cand[c], best = 0, bl = 0, n1 = 0;
            for (var k = 0; k < W; k++) n1 += x[st + k] * x[st + k];
            for (var lag = lo; lag <= hi; lag++) {
                var cc = 0, n2 = 0;
                for (var t = 0; t < W; t++) { cc += x[st + t] * x[st + t + lag]; n2 += x[st + t + lag] * x[st + t + lag]; }
                var v = cc / Math.sqrt(n1 * n2 + 1e-12);
                if (v > best) { best = v; bl = lag; }
            }
            if (best > 0.6 && bl) out.push(sr / bl);
        }
    }

    // Огибающая и речевые кадры одного куска (окно 30 мс, шаг 10 мс).
    function chunkStats(x, sr, thr, acc) {
        var env = frames(x, sr, 0.03, 0.01), n = env.length, H = Math.round(0.01 * sr);
        var speechMask = new Uint8Array(n);
        // мощность речи — по 10-мс шагам, которые сами выше порога
        for (var f = 0; f < n; f++) {
            if (env[f] >= thr) {
                speechMask[f] = 1;
                var a = f * H, s = 0;
                for (var i = a; i < a + H && i < x.length; i++) s += x[i] * x[i];
                acc.speechPow += s; acc.speechN += H; acc.speechFrames++;
                acc.speechLevels.push(env[f]);
            }
        }
        acc.frames += n;
        // паузы: участки ниже порога ≥ 0,25 с, кроме первого и последнего (край куска)
        var run = 0, started = false;
        for (f = 0; f < n; f++) {
            if (!speechMask[f]) run++;
            else {
                if (started && run * 0.01 >= 0.25) acc.pauses.push(run * 0.01);
                run = 0; started = true;
            }
        }
        // разброс громкости: окна 1 с (шаг 0,25 с), где речи не меньше половины
        for (var w0 = 0; w0 + 100 <= n; w0 += 25) {
            var p = 0, c = 0;
            for (var j = w0; j < w0 + 100; j++) if (speechMask[j]) { p += Math.pow(10, env[j] / 10); c++; }
            if (c >= 50) acc.shortLevels.push(pdb(p / c));
        }
        // слоговые ядра — пики огибающей выше 300 Гц с провалом ≥ 2 дБ, не чаще 10 в секунду
        var hp = frames(highpass(x, sr, 300), sr, 0.03, 0.01);
        var last = -1e9, minSince = 1e9;
        for (f = 1; f < hp.length - 1; f++) {
            if (hp[f] < minSince) minSince = hp[f];
            if (hp[f] > hp[f - 1] && hp[f] >= hp[f + 1] && hp[f] > thr + 6 && f - last >= 10 && hp[f] - minSince >= 2) {
                acc.syllables++; last = f; minSince = hp[f];
            }
        }
    }

    // Тишина в начале и в конце: до первого и после последнего кадра выше порога.
    function edgeSilence(mono, sr, thr) {
        var H = Math.round(0.01 * sr), W = Math.round(0.03 * sr), head = 0, tail = 0, i, s, k;
        function lvl(a) { s = 0; for (k = a; k < a + W && k < mono.length; k++) s += mono[k] * mono[k]; return db(Math.sqrt(s / W)); }
        for (i = 0; i + W <= mono.length; i += H) { if (lvl(i) >= thr) break; head = i + H; }
        for (i = mono.length - W; i >= 0; i -= H) { if (lvl(i) >= thr) break; tail = mono.length - i; }
        head = Math.min(head, mono.length); tail = Math.min(tail, mono.length);
        return { head: head / sr, tail: tail / sr };
    }

    /* Полоса записи: выше какой частоты в ней ничего нет. MP3 64 кбит/с режет
       всё выше ~11–15 кГц — за таким эталоном тянуться нельзя: подгонка подняла
       бы верх нашей записи до тишины эталона, то есть подняла бы шипение. */
    function bandwidth(levels, freqs) {
        var ref = 0, n = 0, i;
        for (i = 0; i < freqs.length; i++) if (freqs[i] >= 1000 && freqs[i] <= 4000) { ref += db(levels[i]); n++; }
        ref = n ? ref / n : -100;
        var cut = freqs[freqs.length - 1];
        for (i = 0; i < freqs.length; i++) {
            if (freqs[i] < 4000) continue;
            if (db(levels[i]) < ref - 45) { cut = freqs[i]; break; }
        }
        return cut;
    }

    /* Куски для анализа: целиком, если запись не длиннее maxSec, иначе spans
       кусков, равномерно по длине. Начало и конец не берутся — там заставка. */
    function excerpt(mono, sr, maxSec, spans) {
        maxSec = maxSec || 300; spans = spans || 10;
        if (mono.length <= maxSec * sr) return [mono];
        var len = Math.floor(maxSec / spans * sr), out = [];
        for (var i = 0; i < spans; i++) {
            var c = Math.floor(mono.length * (i + 1) / (spans + 1)), a = Math.max(0, c - (len >> 1));
            out.push(mono.subarray(a, Math.min(mono.length, a + len)));
        }
        return out;
    }

    function concat(chunks) {
        if (chunks.length === 1) return chunks[0];
        var n = 0, i;
        for (i = 0; i < chunks.length; i++) n += chunks[i].length;
        var out = new Float32Array(n), o = 0;
        for (i = 0; i < chunks.length; i++) { out.set(chunks[i], o); o += chunks[i].length; }
        return out;
    }

    function monoBuffer(x, sr) {
        return { sampleRate: sr, numberOfChannels: 1, length: x.length, duration: x.length / sr, getChannelData: function () { return x; } };
    }

    // Средняя форма спектра в полосе, дБ относительно среднего (как у матчера).
    function shapeOf(levels, freqs) {
        var m = 0, n = 0, i;
        for (i = 0; i < freqs.length; i++) if (freqs[i] >= 80 && freqs[i] <= 10000) { m += levels[i]; n++; }
        m = n ? m / n : 1e-9;
        var out = new Float64Array(freqs.length);
        for (i = 0; i < freqs.length; i++) out[i] = db(levels[i] / m);
        return out;
    }
    function bandMean(shape, freqs, lo, hi) {
        var s = 0, n = 0;
        for (var i = 0; i < freqs.length; i++) if (freqs[i] >= lo && freqs[i] < hi) { s += shape[i]; n++; }
        return n ? s / n : 0;
    }
    var REGIONS = [
        { key: 'low', lo: 80, hi: 250 },
        { key: 'body', lo: 250, hi: 2000 },
        { key: 'presence', lo: 2000, hi: 5000 },
        { key: 'top', lo: 5000, hi: 10000 }
    ];

    function measure(chunks, sr, opts) {
        opts = opts || {};
        if (!chunks || !chunks.length) return null;
        var all = concat(chunks);
        if (all.length < sr * 5) return null;                       // меньше 5 с — не о чем говорить
        var g = gate(all, sr);
        var acc = { speechPow: 0, speechN: 0, speechFrames: 0, frames: 0, pauses: [], shortLevels: [], speechLevels: [], syllables: 0 };
        var f0s = [];
        for (var c = 0; c < chunks.length; c++) {
            if (chunks[c].length < sr) continue;
            chunkStats(chunks[c], sr, g.thr, acc);
            f0Of(chunks[c], sr, g.speechDb90 - 6, f0s, Math.ceil(240 / chunks.length));
        }
        if (!acc.speechFrames) return null;
        var rms = 0, peak = 0;
        for (var i = 0; i < all.length; i++) { var v = all[i]; rms += v * v; if (v < 0) v = -v; if (v > peak) peak = v; }
        var short = sortNum(acc.shortLevels), sp = sortNum(acc.speechLevels), pz = sortNum(acc.pauses);
        f0s.sort(function (a, b) { return a - b; });
        var speechSec = acc.speechFrames * 0.01, totalSec = acc.frames * 0.01;
        var pauseSec = 0;
        for (i = 0; i < pz.length; i++) pauseSec += pz[i];

        var tone = null, toneShape = null, bw = null, regions = null;
        var M = MR();
        if (M && M.profileOf) {
            tone = M.profileOf(monoBuffer(all, sr), opts.label || '');
            if (tone && tone.levels) {
                var freqs = M.responseCurve([], sr).freqs;
                toneShape = shapeOf(tone.levels, freqs);
                bw = bandwidth(tone.levels, freqs);
                regions = {};
                for (i = 0; i < REGIONS.length; i++) regions[REGIONS[i].key] = bandMean(toneShape, freqs, REGIONS[i].lo, REGIONS[i].hi);
            }
        }
        return {
            kind: 'speech-profile', v: VERSION, label: opts.label || '', sampleRate: sr,
            seconds: all.length / sr,
            rmsDb: pdb(rms / all.length), peakDb: db(peak), noiseDb: noiseFloor(all, sr),
            speechDb: pdb(acc.speechPow / Math.max(1, acc.speechN)),
            speechMedianDb: pct(sp, 0.5),
            spreadDb: short.length >= 8 ? pct(short, 0.9) - pct(short, 0.1) : null,
            f0: f0s.length >= 10 ? f0s[Math.floor(f0s.length / 2)] : null,
            pace: {
                sylPerSec: speechSec > 5 ? acc.syllables / speechSec : null,
                pauseMedian: pz.length >= 5 ? pct(pz, 0.5) : null,
                pauseShare: totalSec > 0 ? pauseSec / totalSec : null,
                pausesPerMin: totalSec > 30 ? pz.length / (totalSec / 60) : null
            },
            edges: opts.edges || null,
            tone: tone, regions: regions, bandwidthHz: bw
        };
    }

    // ── сравнение и план ──────────────────────────────────────────────────

    function toneDistance(ours, ref, mode) {
        if (!ours || !ref || !ours.regions || !ref.regions) return null;
        var s = 0, n = 0;
        for (var i = 0; i < REGIONS.length; i++) {
            var k = REGIONS[i].key;
            if (mode === 'other' && k === 'low') continue;
            var d = ref.regions[k] - ours.regions[k]; s += d * d; n++;
        }
        return n ? Math.sqrt(s / n) : null;
    }

    function voiceOf(ours, ref) {
        if (!ours || !ref || !ours.f0 || !ref.f0) return { ratio: null, mismatch: false };
        var r = ref.f0 / ours.f0;
        // 1.3 ≈ 4.5 полутона: у одного чтеца между главами f0 гуляет на 5–15 %
        // (замер LibriVox: 116 и 133 Гц у одного человека), у мужчины и женщины —
        // в 1.5–2 раза.
        return { ratio: r, mismatch: r > 1.3 || r < 1 / 1.3 };
    }

    function compare(ours, ref, mode) {
        var rows = [];
        if (!ours || !ref) return rows;
        function row(key, a, b, act) { rows.push({ key: key, ours: a, ref: b, delta: (a != null && b != null) ? b - a : null, act: act || null }); }
        row('loudness', ours.speechDb, ref.speechDb);
        row('noise', ours.noiseDb, ref.noiseDb);
        row('spread', ours.spreadDb, ref.spreadDb);
        row('tone', 0, toneDistance(ours, ref, mode));
        row('pace', ours.pace && ours.pace.sylPerSec, ref.pace && ref.pace.sylPerSec);
        row('pauses', ours.pace && ours.pace.pauseMedian, ref.pace && ref.pace.pauseMedian);
        row('edges', ours.edges, ref.edges);
        return rows;
    }

    /* Маска желаемой кривой для чужого голоса: ниже 70 Гц — только срез (гул),
       300–500 Гц — плавный переход от «не трогать» к «следовать», выше полосы
       любой из записей — не трогать вовсе. */
    function shapeMask(mode, amount, cutHz) {
        return function (freqs, want) {
            for (var i = 0; i < freqs.length; i++) {
                var f = freqs[i], w = 1;
                if (f > cutHz) { want[i] = 0; continue; }
                if (mode === 'other') {
                    if (f < 70) { want[i] = Math.min(0, want[i]) * amount; continue; }
                    if (f < 300) w = 0;
                    else if (f < 500) w = (f - 300) / 200;
                }
                want[i] *= w * amount;
            }
        };
    }

    /* Компрессия речи: детектор RMS 20 мс, мягкое колено 6 дБ, атака 10 мс,
       отпускание 200 мс, только вниз (тихое не поднимается — шум не растёт). */
    function level(x, sr, o) {
        var thr = o.thrDb, ratio = Math.max(1, o.ratio || 1);
        if (ratio <= 1.01) return x;
        var knee = 6, slope = 1 - 1 / ratio;
        var aDet = Math.exp(-1 / (sr * 0.02));
        var aAtt = Math.exp(-1 / (sr * 0.01)), aRel = Math.exp(-1 / (sr * 0.2));
        var y = new Float32Array(x.length), env = 0, gr = 0;
        for (var i = 0; i < x.length; i++) {
            env = aDet * env + (1 - aDet) * x[i] * x[i];
            var over = 10 * Math.log10(env + 1e-20) - thr, want;
            if (over <= -knee / 2) want = 0;
            else if (over >= knee / 2) want = over * slope;
            else { var t = over + knee / 2; want = slope * t * t / (2 * knee); }
            gr = want > gr ? aAtt * gr + (1 - aAtt) * want : aRel * gr + (1 - aRel) * want;
            y[i] = x[i] * Math.pow(10, -gr / 20);
        }
        return y;
    }

    // Разброс громкости после компрессии с данным соотношением (на выдержке).
    function spreadAfter(x, sr, thrDb, ratio) {
        var p = measureSpread(level(x, sr, { thrDb: thrDb, ratio: ratio }), sr);
        return p;
    }
    function measureSpread(x, sr) {
        var g = gate(x, sr), acc = { speechPow: 0, speechN: 0, speechFrames: 0, frames: 0, pauses: [], shortLevels: [], speechLevels: [], syllables: 0 };
        chunkStatsSpreadOnly(x, sr, g.thr, acc);
        var s = sortNum(acc.shortLevels);
        return s.length >= 8 ? pct(s, 0.9) - pct(s, 0.1) : null;
    }
    function chunkStatsSpreadOnly(x, sr, thr, acc) {
        var env = frames(x, sr, 0.03, 0.01), n = env.length;
        for (var w0 = 0; w0 + 100 <= n; w0 += 25) {
            var p = 0, c = 0;
            for (var j = w0; j < w0 + 100; j++) if (env[j] >= thr) { p += Math.pow(10, env[j] / 10); c++; }
            if (c >= 50) acc.shortLevels.push(pdb(p / c));
        }
    }

    /* Подобрать соотношение так, чтобы разброс стал как у эталона: бинарный
       поиск по настоящему результату на выдержке, а не формула — у компрессора
       с атакой и отпусканием «соотношение» и «разброс окон по 1 с» связаны
       нелинейно. */
    function calibrateRatio(x, sr, thrDb, want, maxRatio) {
        var lo = 1, hi = maxRatio || 3, best = null;
        var top = spreadAfter(x, sr, thrDb, hi);
        if (top == null) return null;
        if (top > want) return { ratio: hi, spread: top };          // даже максимум не дотягивает — берём максимум
        for (var it = 0; it < 6; it++) {
            var mid = (lo + hi) / 2, s = spreadAfter(x, sr, thrDb, mid);
            if (s == null) break;
            best = { ratio: mid, spread: s };
            if (s > want) lo = mid; else hi = mid;
        }
        return best;
    }

    function round05(v) { return Math.round(v * 2) / 2; }

    function plan(ours, ref, mode, ctx) {
        ctx = ctx || {};
        mode = mode === 'other' ? 'other' : 'self';
        var out = { mode: mode, notes: [], rmsTarget: null, nr: null, eq: null, lev: null, edges: null, voice: voiceOf(ours, ref) };
        if (!ours || !ref) return out;

        // Громкость. Ухо слышит громкость РЕЧИ, а ACX меряет весь файл с паузами.
        // Наш уровень речи подтягиваем к эталонному, а целевое RMS файла выводим
        // из нашей же доли пауз; ACX сильнее эталона.
        if (isFinite(ref.speechDb) && isFinite(ours.speechDb) && isFinite(ours.rmsDb)) {
            var t = ref.speechDb - (ours.speechDb - ours.rmsDb);
            var tc = clamp(round05(t), ACX.rmsMin + RMS_MARGIN, ACX.rmsMax - RMS_MARGIN);
            out.rmsTarget = tc;
            if (Math.abs(tc - t) > 0.5) out.notes.push(t > tc ? 'rms_capped_high' : 'rms_capped_low');
        }

        // Шум в паузах: не вышли в норму ACX — шумоподавление по силе шума;
        // в норме, но заметно хуже эталона — лёгкое.
        if (isFinite(ours.noiseDb)) {
            if (ours.noiseDb > ACX.noiseMax) out.nr = ours.noiseDb >= -40 ? 'heavy' : (ours.noiseDb >= -50 ? 'medium' : 'light');
            else if (isFinite(ref.noiseDb) && ours.noiseDb - ref.noiseDb >= 10) out.nr = 'light';
        }

        // Тон.
        var M = MR();
        if (M && ctx.targetBuffer && ref.tone && ours.regions && ref.regions) {
            var dist = toneDistance(ours, ref, mode);
            var amount = mode === 'other' ? 0.35 : 1;
            // 0,8 полосы: у кодека перед срезом есть переходная полоса, и в ней «эталон глуше» — это
            // кодек, а не звук (стенд: следуя склону до 0,9 полосы, верх заваливался на 3,9 дБ).
            var cut = Math.min(ours.bandwidthHz || 20000, ref.bandwidthHz || 20000) * 0.8;
            if (dist != null && dist >= 1.2) {
                var spec = M.analyze(ctx.targetBuffer, ref.tone, { shapeWant: shapeMask(mode, amount, cut), capDb: mode === 'other' ? 4 : 8 });
                if (spec && spec.ok) {
                    var bands = [];
                    for (var b = 0; b < spec.bands.length; b++) if (Math.abs(spec.bands[b].gainDb) >= 0.3) bands.push(spec.bands[b]);
                    if (bands.length) out.eq = { bands: bands, amount: amount, distance: dist, cutHz: cut };
                }
            }
            if (cut < 9000) out.notes.push('narrow_band');
        }

        // Разброс громкости. Только сжатие: шире эталона на 1,5 дБ и больше.
        if (ref.spreadDb != null && ours.spreadDb != null && ours.spreadDb - ref.spreadDb >= 1.5 && ctx.levelExcerpt) {
            var thrDb = ours.speechMedianDb;
            var cal = calibrateRatio(ctx.levelExcerpt, ours.sampleRate, thrDb, ref.spreadDb, 3);
            if (cal && cal.ratio > 1.05) out.lev = { thrDb: thrDb, ratio: Math.round(cal.ratio * 100) / 100, spreadBefore: ours.spreadDb, spreadAfter: cal.spread, spreadRef: ref.spreadDb };
        }

        // Тишина по краям — только если эталон её показал (целая глава), и в окне ACX.
        if (ref.edges && isFinite(ref.edges.head) && isFinite(ref.edges.tail)) {
            out.edges = {
                head: clamp(Math.round(ref.edges.head * 10) / 10, ACX.edgeMin, ACX.edgeMax),
                tail: clamp(Math.round(ref.edges.tail * 2) / 2, ACX.edgeMin, ACX.edgeMax)
            };
        }

        if (out.voice.mismatch && mode === 'self') out.notes.push('voice_mismatch');
        return out;
    }

    /* Тишина ровно head / tail секунд. Не хватает — дополняется комнатным тоном
       (тот же шум, что в паузах записи), лишняя — срезается с запасом 50 мс до
       первого звука. Раньше тул ДОБАВЛЯЛ тон к уже имеющейся тишине: глава с
       тремя секундами тишины в конце выходила с шестью, а ACX требует не больше
       пяти. */
    function setEdges(mono, sr, thr, head, tail, tone) {
        var e = edgeSilence(mono, sr, thr);
        var pre = Math.round(0.05 * sr);
        var haveH = Math.round(e.head * sr), haveT = Math.round(e.tail * sr);
        var wantH = Math.round(head * sr), wantT = Math.round(tail * sr);
        // Звука в файле нет совсем — края не определить, оставляем как есть.
        if (haveH + haveT >= mono.length) return mono;
        // Начало звука и конец звука (с запасом), их не режем ни при каком раскладе.
        var startCut = Math.max(0, haveH - wantH);                 // сколько срезать в начале
        var endKeep = mono.length - Math.max(0, haveT - wantT);    // где закончить
        startCut = Math.min(startCut, Math.max(0, haveH - pre));
        endKeep = Math.max(endKeep, Math.min(mono.length, mono.length - haveT + pre));
        var addH = Math.max(0, wantH - haveH), addT = Math.max(0, wantT - haveT);
        var body = mono.subarray(startCut, endKeep);
        var out = new Float32Array(addH + body.length + addT);
        var src = (tone && tone.length) ? tone : null;
        var i;
        for (i = 0; i < addH; i++) out[i] = src ? src[i % src.length] : 0;
        out.set(body, addH);
        for (i = 0; i < addT; i++) out[addH + body.length + i] = src ? src[i % src.length] : 0;
        // стыки — 10 мс, конец файла — 20 мс на ноль
        var xf = Math.round(0.01 * sr);
        if (addH > xf) for (i = 0; i < xf; i++) { var t = i / xf; out[addH - xf + i] *= (1 - t); out[addH + i] *= t; }
        if (addT > xf) for (i = 0; i < xf; i++) { var u = i / xf; out[addH + body.length - xf + i] *= (1 - u); out[addH + body.length + i] *= u; }
        if (startCut > 0) for (i = 0; i < Math.min(xf, body.length); i++) out[addH + i] *= i / xf;
        var fo = Math.min(Math.round(0.02 * sr), out.length);
        for (i = 0; i < fo; i++) out[out.length - fo + i] *= 1 - i / fo;
        return out;
    }

    // ── хранение ──────────────────────────────────────────────────────────

    // Шесть значащих цифр, а не шесть знаков после запятой: уровень тихого верха
    // бывает 1e-7, и округление до 1e-6 превратило бы его в ноль (−200 дБ).
    function arr(a) { return a ? Array.prototype.slice.call(a).map(function (v) { return isFinite(v) ? Number(v.toPrecision(6)) : 0; }) : null; }
    function toJSON(p) {
        if (!p) return null;
        var o = {};
        for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) o[k] = p[k];
        if (p.tone) {
            o.tone = {};
            for (var t in p.tone) if (Object.prototype.hasOwnProperty.call(p.tone, t)) o.tone[t] = p.tone[t];
            o.tone.levels = arr(p.tone.levels);
            o.tone.sideLevels = null;
        }
        return o;
    }
    function fromJSON(o) {
        if (!o || o.kind !== 'speech-profile' || o.v !== VERSION) return null;
        if (o.tone && o.tone.levels && !o.tone.levels.length) return null;
        return o;
    }

    return {
        VERSION: VERSION, ACX: ACX, REGIONS: REGIONS,
        gate: gate, noiseFloor: noiseFloor, edgeSilence: edgeSilence, excerpt: excerpt, concat: concat,
        measure: measure, compare: compare, plan: plan, toneDistance: toneDistance, voiceOf: voiceOf,
        level: level, measureSpread: measureSpread, calibrateRatio: calibrateRatio,
        setEdges: setEdges, shapeMask: shapeMask, bandwidth: bandwidth,
        monoBuffer: monoBuffer, toJSON: toJSON, fromJSON: fromJSON
    };
});
